import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { CtaNote, GuaranteeNote, Reveal, WhatsAppCta, easing } from "@/components/brand";
import { CTA_NOTE, WHATSAPP_SYSTEM, included, pricingTiers, whatsappTierUrl } from "@/lib/offer";

// The recommended plan enters last and a little larger, so the eye lands on it
// after reading the two plans that frame it.
function cardMotion(tier, i) {
  if (!tier.featured) {
    return {
      initial: { opacity: 0, y: 16 },
      whileInView: { opacity: 1, y: 0 },
      transition: { duration: 0.6, delay: i * 0.08, ease: easing },
    };
  }
  return {
    initial: { opacity: 0, y: 24, scale: 0.94 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.7, delay: 0.3, ease: easing },
  };
}

export default function Pricing() {
  return (
    <section id="precos" data-testid="pricing-section" className="bg-muted py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <h2 className="text-3xl font-bold tracking-[-0.03em] text-foreground md:text-5xl">
            Preço claro antes da primeira conversa
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            Valores de referência. O orçamento final depende do escopo, mas você já chega sabendo
            onde está.
          </p>
        </Reveal>

        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-3 lg:gap-0">
          {pricingTiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              viewport={{ once: true, amount: 0.25 }}
              {...cardMotion(tier, i)}
              className={
                tier.featured
                  ? "relative z-10 order-first flex flex-col rounded-xl bg-primary p-8 text-primary-foreground shadow-[0_30px_60px_-30px_rgba(30,64,175,0.7)] lg:order-none lg:-my-6 lg:py-14"
                  : `flex flex-col rounded-xl border border-border bg-background p-8 ${
                      i === 0 ? "lg:rounded-r-none lg:border-r-0" : "lg:rounded-l-none lg:border-l-0"
                    }`
              }
            >
              <div data-testid={`pricing-card-${i}`} className="flex flex-1 flex-col">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold">{tier.name}</h3>
                  {tier.featured && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.6 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ type: "spring", stiffness: 420, damping: 16, delay: 0.85 }}
                      className="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground"
                    >
                      Recomendado
                    </motion.span>
                  )}
                </div>
                <p className={`mt-5 text-sm ${tier.featured ? "text-white/75" : "text-muted-foreground"}`}>
                  {tier.pricePrefix}
                </p>
                <p className={`text-3xl font-bold tracking-[-0.03em] ${tier.featured ? "" : "text-foreground"}`}>
                  {tier.price}
                </p>
                <p className={`mt-1 text-sm ${tier.featured ? "text-white/75" : "text-muted-foreground"}`}>
                  {tier.delivery}
                </p>
                <p className={`mt-6 leading-relaxed ${tier.featured ? "text-white/85" : "text-muted-foreground"}`}>
                  {tier.description}
                </p>

                <ul
                  className={`mt-6 flex-1 space-y-3 border-t pt-6 ${
                    tier.featured ? "border-white/15" : "border-border"
                  }`}
                >
                  {tier.features.map((feature) => (
                    <li
                      key={feature}
                      className={`flex items-start gap-2.5 text-sm ${
                        tier.featured ? "text-white/90" : "text-foreground"
                      }`}
                    >
                      <Check
                        aria-hidden="true"
                        className={`mt-0.5 h-4 w-4 shrink-0 ${tier.featured ? "text-accent" : "text-primary"}`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                <WhatsAppCta
                  href={tier.system ? WHATSAPP_SYSTEM : whatsappTierUrl(tier.name)}
                  variant={tier.featured ? "inverse" : "outline"}
                  size="md"
                  location={`pricing:${tier.name}`}
                  className="mt-8 w-full"
                  data-testid={tier.system ? "pricing-cta-system" : `pricing-cta-${i}`}
                />
              </div>
            </motion.div>
          ))}
        </div>

        <Reveal className="mt-14 grid gap-8 lg:grid-cols-2 lg:gap-16">
          <div>
            <h3 className="font-semibold text-foreground">Todos os sites incluem</h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {included.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-4">
            <GuaranteeNote />
            <CtaNote>{CTA_NOTE}</CtaNote>
            <p className="text-sm text-muted-foreground">
              Precisa de algo menor? Alinhamos o orçamento e fazemos uma oferta especial, dependendo do escopo.{" "}
              <a href="#faq" className="font-semibold text-primary hover:underline">
                Veja as opções
              </a>
              .
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
